import { Injectable, inject } from '@angular/core';
import { FirebaseAwareService } from '../shared/firestore/firebase-aware.service';
import {
  Auth,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithCredential,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signOut,
  user,
  UserCredential,
} from '@angular/fire/auth';
import { Capacitor } from '@capacitor/core';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import type { User } from '@angular/fire/auth';
import { AppCheckService } from './app-check.service';
import { LegalAcceptance, UserService } from './user.service';

@Injectable({ providedIn: 'root' })
export class AuthService extends FirebaseAwareService {
  private auth        = inject(Auth);
  private firestore   = inject(Firestore);
  private router      = inject(Router);
  private userService = inject(UserService);
  private appCheckSvc = inject(AppCheckService);

  readonly currentUser$: Observable<User | null> = user(this.auth);

  private async afterAuth(
    cred: UserCredential,
    returnUrl: string | undefined,
    legal: LegalAcceptance | undefined,
    skipVerifyGate: boolean,
  ): Promise<UserCredential> {
    if (!skipVerifyGate && !cred.user.emailVerified) {
      await sendEmailVerification(cred.user);
      this.router.navigate(
        ['/verificar-email'],
        returnUrl ? { queryParams: { returnUrl } } : {},
      );
      return cred;
    }
    try {
      await this.userService.ensureUserProfile(
        cred.user.uid,
        cred.user.email,
        cred.user.displayName,
        cred.user.photoURL,
        'owner',
        legal,
      );
    } catch (err) {
      console.error('[Auth] Firestore profile failed after sign-in:', err);
      throw new Error('FIRESTORE_AFTER_AUTH');
    }
    this.router.navigateByUrl(returnUrl ?? await this.defaultUrl(cred.user.uid));
    return cred;
  }

  /** Returns the default post-login URL based on the user's role. */
  private async defaultUrl(uid: string): Promise<string> {
    const snap = await this.inCtx(() => getDoc(doc(this.firestore, 'users', uid)));
    const role = snap.exists() ? (snap.data() as Record<string, unknown>)['role'] : null;
    return role === 'user' ? '/mis-reservas' : '/dashboard';
  }

  async signInWithGoogle(returnUrl?: string, legal?: LegalAcceptance): Promise<UserCredential> {
    this.appCheckSvc.warmToken();
    let cred: UserCredential;
    if (Capacitor.isNativePlatform()) {
      const { SocialLogin } = await import('@capgo/capacitor-social-login');
      const webClientId = '558737188944-jghraq8guetemnd2hlk59nhdve9595hk.apps.googleusercontent.com';
      // mode 'online' returns idToken for Firebase Auth (offline only returns serverAuthCode).
      await SocialLogin.initialize({
        google: {
          webClientId,
          mode: 'online',
        },
      });
      const res = await SocialLogin.login({
        provider: 'google',
        options: {
          scopes: ['email', 'profile'],
        },
      });
      if (res.provider !== 'google' || res.result.responseType !== 'online' || !res.result.idToken) {
        throw new Error('Google Sign-In no devolvió idToken');
      }
      const credential = GoogleAuthProvider.credential(res.result.idToken);
      cred = await this.inCtx(() => signInWithCredential(this.auth, credential));
    } else {
      cred = await this.inCtx(() => signInWithPopup(this.auth, new GoogleAuthProvider()));
    }
    return this.afterAuth(cred, returnUrl, legal, true);
  }

  async signInWithEmail(email: string, password: string, returnUrl?: string): Promise<UserCredential> {
    this.appCheckSvc.warmToken();
    const cred = await signInWithEmailAndPassword(this.auth, email, password);
    return this.afterAuth(cred, returnUrl, undefined, false);
  }

  async signUpWithEmail(email: string, password: string, legal?: LegalAcceptance): Promise<UserCredential> {
    this.appCheckSvc.warmToken();
    const cred = await createUserWithEmailAndPassword(this.auth, email, password);
    await this.userService.ensureUserProfile(
      cred.user.uid, cred.user.email, cred.user.displayName, cred.user.photoURL, 'owner', legal,
    );
    await sendEmailVerification(cred.user);
    this.router.navigate(['/verificar-email']);
    return cred;
  }

  signOut(): Promise<void> {
    return signOut(this.auth).then(() => { this.router.navigate(['/']); });
  }
}
